// MCHD Room Scheduler - Room Card Component
// Room-centric display with session lists and assignment status

import React from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  ActionIcon,
  Tooltip,
  Divider,
  Box
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconUsers,
  IconClock,
  IconEdit,
  IconTrash,
  IconSettings,
  IconPlus,
  IconBuilding,
  IconStairs,
  IconDoor
} from '@tabler/icons-react';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
  floor: {
    id: string;
    name: string;
    building: {
      id: string;
      name: string;
    };
  };
  sessionTimes: Array<{
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    isActive: boolean;
  }>;
  vacancyStats?: {
    totalSlots: number;
    vacantSlots: number;
    occupiedSlots: number;
    vacancyRate: number;
    sessionStats: Array<{
      sessionTemplateId: string;
      sessionName: string;
      totalSlots: number;
      usedSlots: number;
      vacantSlots: number;
      vacancyRate: number;
    }>;
  };
}

interface Assignment {
  id: string;
  title: string;
  user: {
    id: string;
    name: string;
    email?: string; // FIXED: Make email optional to match API response
    role?: string;  // FIXED: Add role field from API response
  };
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED'; // FIXED: Match all possible status values
  conflictCode?: string;
}

interface SessionCell {
  sessionTemplateId: string;
  sessionTime: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  dateCells: Array<{
    date: string;
    status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
    assignment?: Assignment | undefined;
    conflicts?: string[] | undefined;
    holiday?: {
      id: string;
      label: string;
    } | undefined;
  }>;
}

interface RoomCardProps {
  /** Room data */
  room: Room;
  /** Selected date range for multi-date view */
  dateRange: [Date, Date];
  /** Session cells for this room */
  sessionCells: SessionCell[];
  /** Whether the room is selected */
  isSelected?: boolean;
  /** Room click handler */
  onRoomClick?: (room: Room) => void;
  /** Session click handler */
  onSessionClick?: (room: Room, sessionTemplateId: string, date: Date) => void;
  /** Room actions handler */
  onRoomAction?: (action: 'edit' | 'delete' | 'configure', room: Room) => void;
  /** Session assignment handler */
  onSessionAssign?: (room: Room, sessionTemplateId: string, date: Date) => void;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format time for display
 */
const formatTime = (time: string): string => {
  return time;
};

/**
 * Get cell display info based on cell status and assignment status
 * UPDATED: 11-10-2025 - Assignments now default to CONFIRMED
 * CONFIRMED shows as "Assigned" (green) - officially assigned
 * @param dateCell - Cell data with status and assignment
 * @returns Display info with text, color, background, and icon
 * @author MCHD Room Scheduler Team
 * @created 09-10-2025 01:00:00
 */
const getCellDisplayInfo = (dateCell: {
  status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  assignment?: Assignment | undefined;
}) => {
  // For non-occupied cells, use simple status mapping
  if (dateCell.status !== 'occupied' || !dateCell.assignment) {
    switch (dateCell.status) {
      case 'empty':
        return { text: 'Available', color: 'green', bgColor: '#f0f9ff', icon: '' };
      case 'blocked':
        return { text: 'Blocked', color: 'red', bgColor: '#fee2e2', icon: '🚫' };
      case 'inactive':
        return { text: 'Inactive', color: 'gray', bgColor: '#f3f4f6', icon: '' };
      case 'conflict':
        return { text: 'Conflict', color: 'orange', bgColor: '#fff4e6', icon: '⚠️' };
      case 'holiday':
        return { text: 'Holiday', color: 'purple', bgColor: '#faf5ff', icon: '🎉' };
      default:
        return { text: 'Unknown', color: 'gray', bgColor: '#ffffff', icon: '' };
    }
  }
  
  // For occupied cells, use ASSIGNMENT STATUS to determine display
  // FIXED: 14-10-2025 - Only handle actual database statuses (CONFIRMED, CANCELLED)
  switch (dateCell.assignment.status) {
    case 'CONFIRMED':
      return {
        text: 'Assigned',                   // Confirmed and scheduled
        color: 'green',
        bgColor: '#d1fae5',                 // Light green - approved
        hoverBgColor: '#a7f3d0',            // Darker green on hover
        icon: '✓',
        tooltip: 'Confirmed and scheduled'
      };
    case 'CANCELLED':
      return {
        text: 'Cancelled',                  // Assignment removed
        color: 'gray',
        bgColor: '#f3f4f6',                 // Light gray - inactive
        hoverBgColor: '#e5e7eb',
        icon: '✕',
        tooltip: 'Assignment removed'
      };
    default:
      return {
        text: 'Assigned',                   // Default to assigned for any unknown status
        color: 'green',
        bgColor: '#d1fae5',
        hoverBgColor: '#a7f3d0',
        icon: '✓',
        tooltip: 'Confirmed and scheduled'
      };
  }
};

// =============================================================================
// ROOM CARD COMPONENT
// =============================================================================

export const RoomCard: React.FC<RoomCardProps> = ({
  room,
  dateRange: _dateRange, // Mark as unused since we use server-determined status
  sessionCells,
  isSelected = false,
  onRoomClick,
  onSessionClick,
  onRoomAction,
  onSessionAssign
}) => {
  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderSessionCell = (sessionTime: Room['sessionTimes'][0]) => {
    // Find the session cell data for this session time
    const sessionCell = sessionCells.find(
      c => c.sessionTemplateId === sessionTime.id
    );

    if (!sessionCell) {
      return (
        <Box
          key={sessionTime.id}
          p="sm"
          style={{
            border: '1px solid #e9ecef',
            borderRadius: '6px',
            backgroundColor: '#f8f9fa',
            cursor: 'not-allowed'
          }}
        >
          <Text size="xs" c="dimmed" ta="center">
            No data
          </Text>
        </Box>
      );
    }

    // Render date cells for each date in the range
    return (
      <Stack gap="xs" key={sessionTime.id}>
        {sessionCell.dateCells.map((dateCell) => {
          // Check if date is in the past
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const assignmentDate = new Date(dateCell.date);
          assignmentDate.setHours(0, 0, 0, 0);
          const isPastDate = assignmentDate < today;
    
  // ENHANCED: 09-10-2025 01:00:00 - Get display info based on assignment status
          const displayInfo = getCellDisplayInfo(dateCell);

  const handleClick = () => {
    if (isPastDate) {
      // ENHANCED: 08-10-2025 23:20:00 - Show helpful warning for past date clicks
      notifications.show({
        title: 'Past Date Not Allowed',
        message: 'Cannot create assignments for past dates. Please use the "Create Assignment" button for today or future dates.',
        color: 'orange',
        icon: <IconClock size={16} />,
        autoClose: 5000,
        withCloseButton: true
      });
      return;
    }
      
      // ENHANCED: 08-10-2025 22:15:00 - Smart room interactions based on status
      if (dateCell.status === 'empty') {
        onSessionAssign?.(room, sessionTime.id, new Date(dateCell.date));
      } else if (dateCell.status === 'occupied') {
        // Show assignment details instead of opening assignment modal
        if (dateCell.assignment) {
          // TODO: Open assignment details modal/drawer
          console.log('View assignment details:', dateCell.assignment);
        }
      } else {
        onSessionClick?.(room, sessionTime.id, new Date(dateCell.date));
      }
    };

    return (
      <Box
        key={`${sessionTime.id}-${dateCell.date}`}
        p="sm"
        style={{
          border: '1px solid #e9ecef',
          borderRadius: '6px',
          backgroundColor: isPastDate ? '#f8f9fa' : displayInfo.bgColor,
          cursor: isPastDate ? 'not-allowed' : (dateCell.status === 'empty' ? 'pointer' : dateCell.status === 'occupied' ? 'pointer' : 'default'),
          transition: 'all 0.2s ease',
          opacity: isPastDate ? 0.6 : 1
        }}
        onClick={handleClick}
        onMouseEnter={(e) => {
          // ENHANCED: 08-10-2025 23:20:00 - Skip hover effects for past dates
          if (isPastDate) {
            return;
          }
          
          if (dateCell.status === 'empty') {
            e.currentTarget.style.backgroundColor = '#e0f2fe';
            e.currentTarget.style.borderColor = '#0ea5e9';
          } else if (dateCell.status === 'occupied' && displayInfo.hoverBgColor) {
            e.currentTarget.style.backgroundColor = displayInfo.hoverBgColor;
            e.currentTarget.style.borderColor = displayInfo.color === 'yellow' ? '#f59e0b' : '#10b981';
          }
        }}
        onMouseLeave={(e) => {
          // ENHANCED: 08-10-2025 23:20:00 - Skip hover effects for past dates
          if (isPastDate) {
            return;
          }
          
          if (dateCell.status === 'empty') {
            e.currentTarget.style.backgroundColor = '#f0f9ff';
            e.currentTarget.style.borderColor = '#e9ecef';
          } else if (dateCell.status === 'occupied') {
            e.currentTarget.style.backgroundColor = displayInfo.bgColor;
            e.currentTarget.style.borderColor = '#e9ecef';
          }
        }}
      >
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs" style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {sessionTime.name}
              </Text>
              <Text size="xs" c="dimmed">
                {formatTime(sessionTime.startTime)} - {formatTime(sessionTime.endTime)}
              </Text>
              <Text 
                size="xs" 
                c={isPastDate ? 'red' : 'blue'} 
                fw={500}
                style={{ 
                  textDecoration: isPastDate ? 'line-through' : 'none',
                  opacity: isPastDate ? 0.7 : 1
                }}
              >
                {new Date(dateCell.date).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
                {isPastDate && ' (Past)'}
              </Text>
              {/* ENHANCED: 09-10-2025 01:00:00 - Show assignment details with status-aware styling */}
              {dateCell.status === 'occupied' && dateCell.assignment && (
                <Group gap="xs">
                  <Text 
                    size="xs" 
                    c={displayInfo.color === 'yellow' ? 'orange' : displayInfo.color === 'green' ? 'green' : 'dimmed'} 
                    fw={500}
                  >
                    {displayInfo.icon} {dateCell.assignment.user?.name || 'Unknown'}
                  </Text>
                </Group>
              )}
            </Stack>
            
            <Badge
              size="sm"
              color={displayInfo.color}
              variant="filled"
            >
              {displayInfo.text}
            </Badge>
          </Group>

          {dateCell.assignment && (
            <Divider />
          )}

          {dateCell.assignment && (
            <Stack gap="xs">
              <Text size="xs" fw={500}>
                {dateCell.assignment.user.name}
              </Text>
              <Text size="xs" c="dimmed">
                {dateCell.assignment.title}
              </Text>
            </Stack>
          )}

          {dateCell.status === 'empty' && (
            <Tooltip 
              label={isPastDate ? "Cannot assign to past dates. Please select today or a future date." : "Click to assign a doctor to this session"}
              disabled={!isPastDate}
            >
              <Button
                size="xs"
                variant="light"
                color={isPastDate ? 'gray' : 'blue'}
                leftSection={<IconPlus size={12} />}
                disabled={isPastDate}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isPastDate) {
                    onSessionAssign?.(room, sessionTime.id, new Date(dateCell.date));
                  }
                }}
              >
                {isPastDate ? 'Past Date' : 'Assign'}
              </Button>
            </Tooltip>
          )}

          {dateCell.conflicts && dateCell.conflicts.length > 0 && (
            <Box
              p="xs"
              style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '4px'
              }}
            >
              <Text size="xs" c="orange">
                Conflicts: {dateCell.conflicts.join(', ')}
              </Text>
            </Box>
          )}

          {dateCell.holiday && (
            <Box
              p="xs"
              style={{
                backgroundColor: '#f3e8ff',
                border: '1px solid #a855f7',
                borderRadius: '4px'
              }}
            >
              <Text size="xs" c="purple">
                {dateCell.holiday.label}
              </Text>
            </Box>
          )}
        </Stack>
      </Box>
    );
        })}
      </Stack>
    );
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{
        backgroundColor: isSelected ? '#f0f5fa' : '#ffffff',
        border: isSelected ? '2px solid #0ea5e9' : '1px solid #e9ecef',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onClick={() => onRoomClick?.(room)}
    >
      <Stack gap="md">
        {/* Room Header */}
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group gap="sm">
              <IconDoor size={18} color="var(--mantine-color-blue-6)" />
              <Text fw={600} size="lg">
                {room.number} - {room.name}
              </Text>
            </Group>
            
            <Group gap="sm">
              <Badge
                color={room.isActive ? 'green' : 'red'}
                variant="light"
                size="sm"
              >
                {room.isActive ? 'Active' : 'Inactive'}
              </Badge>
              
              <Badge
                color="blue"
                variant="light"
                size="sm"
                leftSection={<IconUsers size={12} />}
              >
                {room.capacity} Capacity
              </Badge>
              
              {/* Vacancy Stats Badge */}
              {room.vacancyStats && (
                <Tooltip 
                  label={`${room.vacancyStats.vacantSlots} of ${room.vacancyStats.totalSlots} slots available for assignment (future dates only, past dates are read-only)`}
                  multiline
                  w={280}
                >
                  <Badge
                    color={
                      room.vacancyStats.vacancyRate > 70 ? 'green' :
                      room.vacancyStats.vacancyRate > 30 ? 'yellow' : 'red'
                    }
                    variant="dot"
                    size="sm"
                    style={{ cursor: 'help' }}
                  >
                    {room.vacancyStats.vacantSlots}/{room.vacancyStats.totalSlots} Available
                  </Badge>
                </Tooltip>
              )}
            </Group>

            <Group gap="xs">
              <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
              <Text size="sm" c="dimmed">
                {room.floor.building.name}
              </Text>
              <IconStairs size={14} color="var(--mantine-color-gray-6)" />
              <Text size="sm" c="dimmed">
                {room.floor.name}
              </Text>
            </Group>
            
            {/* Per-Session Vacancy Indicators */}
            {room.vacancyStats && room.vacancyStats.sessionStats.length > 0 && (
              <Group gap={6} mt={4}>
                {room.vacancyStats.sessionStats.map(stat => (
                  <Tooltip
                    key={stat.sessionTemplateId}
                    label={`${stat.sessionName}: ${stat.vacantSlots}/${stat.totalSlots} available (${stat.vacancyRate}%)`}
                    position="top"
                    withArrow
                  >
                    <Box
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: 
                          stat.vacancyRate > 70 ? '#51cf66' :
                          stat.vacancyRate > 30 ? '#ffd43b' : 
                          stat.vacancyRate > 0 ? '#ff8787' : '#868e96',
                        cursor: 'help',
                        border: '1px solid rgba(0,0,0,0.1)'
                      }}
                    />
                  </Tooltip>
                ))}
              </Group>
            )}
          </Stack>

          {/* Room Actions */}
          <Group gap="xs">
            <Tooltip label="Edit room">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRoomAction?.('edit', room);
                }}
              >
                <IconEdit size={14} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Configure room">
              <ActionIcon
                variant="subtle"
                color="orange"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRoomAction?.('configure', room);
                }}
              >
                <IconSettings size={14} />
              </ActionIcon>
            </Tooltip>
            
            <Tooltip label="Delete room">
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRoomAction?.('delete', room);
                }}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <Divider />

        {/* Session Times */}
        {room.sessionTimes.length > 0 ? (
          <Stack gap="sm">
            <Group gap="sm" align="center">
              <IconClock size={16} color="var(--mantine-color-gray-6)" />
              <Text size="sm" fw={500}>
                Session Times ({room.sessionTimes.length})
              </Text>
            </Group>
            
            <Group gap="sm" style={{ flexWrap: 'wrap' }}>
              {room.sessionTimes.map(renderSessionCell)}
            </Group>
          </Stack>
        ) : (
          <Box
            p="md"
            style={{
              backgroundColor: '#f8f9fa',
              border: '1px dashed #dee2e6',
              borderRadius: '6px',
              textAlign: 'center'
            }}
          >
            <Text size="sm" c="dimmed">
              No session times configured
            </Text>
            <Button
              size="xs"
              variant="light"
              color="blue"
              mt="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRoomAction?.('configure', room);
              }}
            >
              Configure Sessions
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default RoomCard;
